import {
  computeClearBusLaneY,
  computeSmartOrthogonalRoute,
  routeIntersectsAnyRect,
  type SpatialRect,
} from '../../../core/layout/spatialIntent';
import { isOrgPrimaryReportingEdge } from '../connectors/routingPolicies';
import { resolveOrgPersonPhotoTopCenterTopAnchor } from '../models/orgPersonPhotoAnchors';
import type { OrgPersonCardViewMode } from '../models/orgChartShapeVariants';

export type OrgRouteSide = 'top' | 'right' | 'bottom' | 'left';

export interface OrgRouteNode {
  id: string;
  type?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  visible?: boolean;
  attrs?: Record<string, unknown>;
}

export interface OrgRouteEdge {
  id: string;
  type?: string;
  from?: string;
  to?: string;
  fromAnchor?: OrgRouteSide;
  toAnchor?: OrgRouteSide;
  fromOffset?: number;
  toOffset?: number;
  fromAnchorLocked?: boolean;
  toAnchorLocked?: boolean;
  attrs?: Record<string, unknown>;
}

export interface OrgReportingRoutePlanInput {
  edge: OrgRouteEdge;
  nodes: readonly OrgRouteNode[];
  edges: readonly OrgRouteEdge[];
  fromNode: OrgRouteNode;
  toNode: OrgRouteNode;
  autoLayoutEnabled: boolean;
  layoutSource?: string;
  fromAnchor?: OrgRouteSide | null;
  toAnchor?: OrgRouteSide | null;
  fromOffset?: number | null;
  toOffset?: number | null;
  isPreviewingEndpoint?: boolean;
  childIsParentOverride?: boolean;
  /** Org vertical-spacing setting, so the bus lane scales with the layout. */
  verticalSpacing?: number | null;
  /** Resolved compact/full card mode used by org person-card renderers. */
  orgPersonCardViewMode?: OrgPersonCardViewMode;
}

export interface OrgReportingRoutePlan {
  type: 'roundedOrthogonal';
  points: number[];
  reason: string;
}

/**
 * Vertical room the reporting arrowhead needs below the parent card. The
 * arrowhead is drawn at the parent edge, so a shorter stem makes the horizontal
 * bus line cut through it.
 */
export const ORG_BUS_ARROW_CLEARANCE = 14;

const DEFAULT_ORG_BUS_PREFERRED_GAP = 20;

/**
 * Keeps the bus lane roughly a fifth of the way down the level gap, so tight
 * layouts stay compact and generous ones do not leave the lane hugging the
 * parent card.
 */
export const resolveOrgBusPreferredGap = (verticalSpacing?: number | null): number => {
  if (typeof verticalSpacing !== 'number' || !Number.isFinite(verticalSpacing)) {
    return DEFAULT_ORG_BUS_PREFERRED_GAP;
  }
  const scaled = Math.round(verticalSpacing * 0.2);
  return Math.min(64, Math.max(DEFAULT_ORG_BUS_PREFERRED_GAP, scaled));
};

export const ORG_COLLAPSED_DOT_GRID_SHAPE_TYPE = 'org_collapsed_dot_grid';
export const ORG_COLLAPSED_DOT_GRID_ID_PREFIX = '__org_dot_grid__';

const ORG_OCCUPANCY_NODE_TYPES = new Set([
  'org_person',
  'org_person_photo',
  'org_person_photo_left',
  'org_person_photo_right',
  'org_person_photo_inner_left',
  'org_person_text',
  'org_position',
  'org_department',
  'org_team',
  'org_project',
  'org_role_profile',
]);

export const isOrgRoutingOccupancyNodeType = (type: string): boolean =>
  ORG_OCCUPANCY_NODE_TYPES.has(type);

export const isOrgRoutingOccupancyShape = (shape: {
  type?: string;
  id?: string;
}): boolean => {
  const nodeType = String(shape.type ?? '');
  if (nodeType === ORG_COLLAPSED_DOT_GRID_SHAPE_TYPE) return false;
  if (String(shape.id ?? '').startsWith(ORG_COLLAPSED_DOT_GRID_ID_PREFIX)) return false;
  return isOrgRoutingOccupancyNodeType(nodeType);
};

const ORG_ROUTE_CLEARANCE = 12;
const ORG_ROUTE_OCCUPANCY_PADDING = 2;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const clampNumber = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const rectForNode = (node: OrgRouteNode): SpatialRect => ({
  x: node.x,
  y: node.y,
  width: node.width,
  height: node.height,
});

const pointOnRect = (rect: SpatialRect, side: OrgRouteSide, offset = 0.5): { x: number; y: number } => {
  const t = Math.max(0, Math.min(1, Number.isFinite(offset) ? offset : 0.5));
  switch (side) {
    case 'top':
      return { x: rect.x + rect.width * t, y: rect.y };
    case 'right':
      return { x: rect.x + rect.width, y: rect.y + rect.height * t };
    case 'bottom':
      return { x: rect.x + rect.width * t, y: rect.y + rect.height };
    case 'left':
      return { x: rect.x, y: rect.y + rect.height * t };
  }
};

const pointOnOrgNode = (
  node: OrgRouteNode,
  side: OrgRouteSide,
  offset = 0.5,
  viewMode?: OrgPersonCardViewMode,
): { x: number; y: number } => {
  const rect = rectForNode(node);
  if (side === 'top' && String(node.type ?? '') === 'org_person_photo') {
    return resolveOrgPersonPhotoTopCenterTopAnchor(rect.x, rect.y, rect.width, rect.height, offset, viewMode);
  }
  return pointOnRect(rect, side, offset);
};

const isVisibleOccupancyNode = (node: OrgRouteNode): boolean => {
  if (node.visible === false) return false;
  const attrs = node.attrs ?? {};
  if (attrs.visible === false || attrs.hidden === true) return false;
  return isOrgRoutingOccupancyShape(node);
};

const isOccupiesEdge = (edge: OrgRouteEdge): boolean => {
  const relation = typeof edge.attrs?.relation === 'string' ? edge.attrs.relation : undefined;
  return edge.type === 'occupies' || relation === 'occupies';
};

const computeReportingTargets = (
  nodes: readonly OrgRouteNode[],
  edges: readonly OrgRouteEdge[],
): Map<string, string> => {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const nodeTypeById = new Map(nodes.map((node) => [node.id, String(node.type ?? '')]));
  const occupiesByPerson = new Map<string, string>();

  edges.forEach((edge) => {
    if (!isOccupiesEdge(edge)) return;
    if (!edge.from || !edge.to) return;
    if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) return;
    occupiesByPerson.set(edge.from, edge.to);
  });

  const targets = new Map<string, string>();
  nodes.forEach((node) => {
    if (nodeTypeById.get(node.id) === 'org_position') {
      targets.set(node.id, node.id);
      return;
    }
    const positionId = occupiesByPerson.get(node.id);
    targets.set(node.id, positionId && nodeIds.has(positionId) ? positionId : node.id);
  });
  return targets;
};

const routeHitsOccupancy = (
  points: readonly number[],
  nodes: readonly OrgRouteNode[],
  excludedNodeIds: ReadonlySet<string> = new Set(),
): boolean => {
  const rects = nodes
    .filter(isVisibleOccupancyNode)
    .filter((node) => !excludedNodeIds.has(node.id))
    .map(rectForNode)
    .filter((rect) => (
      Number.isFinite(rect.x) &&
      Number.isFinite(rect.y) &&
      rect.width > 0 &&
      rect.height > 0
    ));
  return routeIntersectsAnyRect(points, rects, ORG_ROUTE_OCCUPANCY_PADDING);
};

const chooseFirstClearRoute = (
  candidates: Array<{ reason: string; points: number[] }>,
  nodes: readonly OrgRouteNode[],
  excludedNodeIds: ReadonlySet<string>,
): OrgReportingRoutePlan | null => {
  for (const candidate of candidates) {
    if (!routeHitsOccupancy(candidate.points, nodes, excludedNodeIds)) {
      return { type: 'roundedOrthogonal', points: candidate.points, reason: candidate.reason };
    }
  }
  return null;
};

export const planOrgPrimaryReportingRoute = (
  input: OrgReportingRoutePlanInput,
): OrgReportingRoutePlan | null => {
  const {
    edge,
    nodes,
    edges,
    fromNode,
    toNode,
    autoLayoutEnabled,
    layoutSource,
    isPreviewingEndpoint = false,
    orgPersonCardViewMode,
  } = input;

  if (!isOrgPrimaryReportingEdge(edge)) return null;
  if (edge.attrs?.projectedLayout === 'custom' && isPreviewingEndpoint) {
    const route = computeSmartOrthogonalRoute({
      sourceRect: rectForNode(fromNode), targetRect: rectForNode(toNode),
      obstacles: nodes.filter((node) => node.id !== fromNode.id && node.id !== toNode.id)
        .filter(isVisibleOccupancyNode).map(rectForNode), gap: ORG_ROUTE_CLEARANCE,
    });
    const points = [...route.points];
    const start = pointOnOrgNode(fromNode, route.sourceAnchor);
    const end = pointOnOrgNode(toNode, route.targetAnchor);
    points[0] = start.x;
    points[1] = start.y;
    points[points.length - 2] = end.x;
    points[points.length - 1] = end.y;
    return { type: 'roundedOrthogonal', points, reason: 'custom-layout' };
  }
  if (isPreviewingEndpoint) return null;

  const fromRect = rectForNode(fromNode);
  const toRect = rectForNode(toNode);
  const isBelowParent = fromRect.y > toRect.y + toRect.height;
  if (!isBelowParent) return null;

  const fromAnchor = input.fromAnchor ?? edge.fromAnchor ?? null;
  const toAnchor = input.toAnchor ?? edge.toAnchor ?? null;
  const fromOffset = isFiniteNumber(input.fromOffset) ? input.fromOffset : edge.fromOffset;
  const toOffset = isFiniteNumber(input.toOffset) ? input.toOffset : edge.toOffset;
  const fromOffsetIsDefault = !isFiniteNumber(fromOffset) || Math.abs(fromOffset - 0.5) < 0.01;
  const toOffsetIsDefault = !isFiniteNumber(toOffset) || Math.abs(toOffset - 0.5) < 0.01;
  const toAnchorIsDefault = toAnchor === 'bottom' || toAnchor == null;
  const fromAnchorIsOrgBusDefault =
    fromAnchor === 'top' ||
    fromAnchor === 'left' ||
    fromAnchor === 'right' ||
    fromAnchor == null;

  const reportingTargets = computeReportingTargets(nodes, edges);
  const resolveReportingId = (nodeId: string | undefined): string | undefined => {
    if (!nodeId) return undefined;
    return reportingTargets.get(nodeId) ?? nodeId;
  };
  const fromReportingId = resolveReportingId(fromNode.id);
  const toReportingId = resolveReportingId(toNode.id);
  const reportsToNode = (candidate: OrgRouteEdge, managerId: string | undefined): boolean => (
    Boolean(managerId) &&
    isOrgPrimaryReportingEdge(candidate) &&
    resolveReportingId(candidate.to) === managerId
  );
  const directChildRects = edges
    .filter((candidate) => reportsToNode(candidate, toReportingId))
    .map((candidate): SpatialRect | null => {
      const childReportingId = resolveReportingId(candidate.from);
      if (childReportingId === fromReportingId) return fromRect;
      const childNode =
        nodes.find((node) => node.id === candidate.from) ??
        nodes.find((node) => resolveReportingId(node.id) === childReportingId);
      return childNode ? rectForNode(childNode) : null;
    })
    .filter((rect): rect is SpatialRect => rect !== null);
  const relevantDirectChildRects = !autoLayoutEnabled
    ? directChildRects.filter((rect) => rect.y > toRect.y + toRect.height)
    : directChildRects;

  const parentBottomCenter = pointOnOrgNode(toNode, 'bottom', 0.5, orgPersonCardViewMode);
  const busX = parentBottomCenter.x;
  const busY = computeClearBusLaneY({
    parentBottomY: parentBottomCenter.y,
    childRects: relevantDirectChildRects,
    preferredGap: resolveOrgBusPreferredGap(input.verticalSpacing),
    minimumGap: ORG_BUS_ARROW_CLEARANCE,
  });
  const busXPassesThroughChild = busX > fromRect.x + 1 && busX < fromRect.x + fromRect.width - 1;
  const childIsParentLive = edges.some((candidate) => reportsToNode(candidate, fromReportingId));
  const childIsParent = typeof input.childIsParentOverride === 'boolean'
    ? input.childIsParentOverride
    : childIsParentLive;

  const branchAnchorIsLayoutAuthored =
    !edge.fromAnchorLocked &&
    !edge.toAnchorLocked &&
    fromAnchorIsOrgBusDefault &&
    (
      layoutSource === 'elk' ||
      fromAnchor === 'left' ||
      fromAnchor === 'right'
    );
  const matchesRoleDefault = childIsParent
    ? (fromAnchor === 'top' || fromAnchor == null || branchAnchorIsLayoutAuthored)
    : fromAnchorIsOrgBusDefault;
  const matchesRoleDefaultFull =
    matchesRoleDefault && toAnchorIsDefault && fromOffsetIsDefault && toOffsetIsDefault;

  // Tree OFF can include hand-drawn org relationships. Locked endpoints or
  // non-default offsets are manual intent, so the generic/manual router owns them.
  if (!autoLayoutEnabled && (edge.fromAnchorLocked || edge.toAnchorLocked)) return null;
  if (!autoLayoutEnabled && !matchesRoleDefaultFull) return null;

  const childSidesByBusDistance = () => {
    const childLeftCenter = pointOnRect(fromRect, 'left');
    const childRightCenter = pointOnRect(fromRect, 'right');
    return [
      { side: 'left' as const, point: childLeftCenter, distance: Math.abs(busX - childLeftCenter.x) },
      { side: 'right' as const, point: childRightCenter, distance: Math.abs(busX - childRightCenter.x) },
    ].sort((left, right) => {
      if (left.distance !== right.distance) return left.distance - right.distance;
      if (left.side === fromAnchor) return -1;
      if (right.side === fromAnchor) return 1;
      return left.side === 'left' ? -1 : 1;
    });
  };

  const routeSingleChildFromTop = () => {
    const childTopAnchor = pointOnOrgNode(fromNode, 'top', 0.5, orgPersonCardViewMode);
    const childTopOnBus = {
      x: clampNumber(busX, fromRect.x, fromRect.x + fromRect.width),
      y: childTopAnchor.y,
    };
    return [
      childTopOnBus.x, childTopOnBus.y,
      busX, parentBottomCenter.y,
    ];
  };

  const routeChildTopViaBus = () => {
    const childTopCenter = pointOnOrgNode(fromNode, 'top', 0.5, orgPersonCardViewMode);
    return [
      childTopCenter.x, childTopCenter.y,
      childTopCenter.x, busY,
      busX, busY,
      busX, parentBottomCenter.y,
    ];
  };

  const routeChildCenterTopViaBus = () => {
    const childTopAnchor = pointOnOrgNode(fromNode, 'top', 0.5, orgPersonCardViewMode);
    const childTopOnBus = {
      x: clampNumber(busX, fromRect.x, fromRect.x + fromRect.width),
      y: childTopAnchor.y,
    };
    return [
      childTopOnBus.x, childTopOnBus.y,
      childTopOnBus.x, busY,
      busX, busY,
      busX, parentBottomCenter.y,
    ];
  };

  const routeChildSideViaBus = (side: 'left' | 'right', sidePoint: { x: number; y: number }) => {
    const laneX = side === 'left'
      ? sidePoint.x - ORG_ROUTE_CLEARANCE
      : sidePoint.x + ORG_ROUTE_CLEARANCE;
    return [
      sidePoint.x, sidePoint.y,
      laneX, sidePoint.y,
      laneX, busY,
      busX, busY,
      busX, parentBottomCenter.y,
    ];
  };

  const routeSideOnParentTrunk = (sidePoint: { x: number; y: number }) => [
    sidePoint.x, sidePoint.y,
    busX, sidePoint.y,
    busX, parentBottomCenter.y,
  ];

  const directChildrenFormHorizontalRow = (() => {
    if (relevantDirectChildRects.length <= 1) return false;
    const centers = relevantDirectChildRects.map((rect) => ({
      x: rect.x + rect.width / 2,
      y: rect.y + rect.height / 2,
    }));
    const spreadX = Math.max(...centers.map((center) => center.x)) -
      Math.min(...centers.map((center) => center.x));
    const spreadY = Math.max(...centers.map((center) => center.y)) -
      Math.min(...centers.map((center) => center.y));
    return spreadX >= spreadY;
  })();

  const childInVerticalStackColumn = (() => {
    if (relevantDirectChildRects.length <= 1) return false;
    const fromCenterX = fromRect.x + fromRect.width / 2;
    return relevantDirectChildRects.some((rect) => {
      if (rect === fromRect) return false;
      const rectCenterX = rect.x + rect.width / 2;
      const horizontalOverlap = Math.min(fromRect.x + fromRect.width, rect.x + rect.width) -
        Math.max(fromRect.x, rect.x);
      const minWidth = Math.min(fromRect.width, rect.width);
      const centersAligned = Math.abs(fromCenterX - rectCenterX) <= Math.max(24, minWidth * 0.25);
      const overlapsColumn = horizontalOverlap >= minWidth * 0.5;
      const verticallySeparated =
        rect.y >= fromRect.y + fromRect.height ||
        fromRect.y >= rect.y + rect.height;
      return verticallySeparated && (centersAligned || overlapsColumn);
    });
  })();

  const candidates: Array<{ reason: string; points: number[] }> = [];
  const sideCandidates = childSidesByBusDistance();
  const parentTrunkClearsStack = (side: 'left' | 'right'): boolean => {
    if (!childInVerticalStackColumn) return false;
    return relevantDirectChildRects.every((rect) => {
      if (side === 'left') return busX <= rect.x - ORG_ROUTE_CLEARANCE;
      return busX >= rect.x + rect.width + ORG_ROUTE_CLEARANCE;
    });
  };
  const addSideCandidates = (reasonPrefix: 'fallback-child' | 'vertical-stack') => {
    sideCandidates.forEach((candidate) => {
      if (reasonPrefix === 'vertical-stack' && parentTrunkClearsStack(candidate.side)) {
        candidates.push({
          reason: 'vertical-stack-parent-trunk',
          points: routeSideOnParentTrunk(candidate.point),
        });
      }
      candidates.push(
        { reason: `${reasonPrefix}-side-via-bus`, points: routeChildSideViaBus(candidate.side, candidate.point) },
      );
    });
  };

  if (childInVerticalStackColumn) {
    addSideCandidates('vertical-stack');
  } else {
    if (childIsParent) {
      candidates.push({ reason: 'branch-top-via-bus', points: routeChildTopViaBus() });
    } else if (relevantDirectChildRects.length <= 1 && busXPassesThroughChild) {
      candidates.push({ reason: 'single-child-top', points: routeSingleChildFromTop() });
    } else {
      candidates.push({
        reason: directChildrenFormHorizontalRow ? 'horizontal-row-top-via-bus' : 'child-top-via-bus',
        points: busXPassesThroughChild ? routeChildCenterTopViaBus() : routeChildTopViaBus(),
      });
    }
    addSideCandidates('fallback-child');
  }

  const clearCandidate = chooseFirstClearRoute(candidates, nodes, new Set([fromNode.id, toNode.id]));
  if (clearCandidate) return clearCandidate;

  const obstacleRects = nodes
    .filter((node) => node.id !== fromNode.id && node.id !== toNode.id)
    .filter(isVisibleOccupancyNode)
    .map(rectForNode);
  const smartRoute = computeSmartOrthogonalRoute({
    sourceRect: fromRect,
    targetRect: toRect,
    preferredSourceAnchors: ['left', 'right', 'top'],
    preferredTargetAnchors: ['bottom'],
    obstacles: obstacleRects,
    gap: 12,
  });

  return {
    type: 'roundedOrthogonal',
    points: smartRoute.points,
    reason: 'smart-orthogonal-fallback',
  };
};
